import { createContext, useContext, useEffect, useMemo, useReducer, useState, type Dispatch, type ReactNode } from 'react';
import { readAutosave, saveAutosave } from '../state/autosave';
import { createDefaultProject, projectReducer } from '../state/projectReducer';
import type { ProjectAction, ProjectState } from '../state/projectTypes';

interface StoreValue {
  readonly state: ProjectState;
  readonly dispatch: Dispatch<ProjectAction>;
}

const StoreContext = createContext<StoreValue | undefined>(undefined);

type ProjectInitialization =
  | { readonly state: ProjectState; readonly autosaveBlocked: false }
  | { readonly state: ProjectState; readonly autosaveBlocked: true; readonly loadError: string };

function initProject(): ProjectInitialization {
  const result = readAutosave();
  if (result.status === 'loaded') return { state: result.state, autosaveBlocked: false };
  if (result.status === 'empty') return { state: createDefaultProject(), autosaveBlocked: false };
  return { state: createDefaultProject(), autosaveBlocked: true, loadError: result.message };
}

export function StoreProvider({ children }: { children: ReactNode }): JSX.Element {
  const [initialization] = useState(initProject);
  const [state, dispatch] = useReducer(projectReducer, initialization.state);

  useEffect(() => {
    if (!initialization.autosaveBlocked) saveAutosave(state);
  }, [initialization.autosaveBlocked, state]);

  const value = useMemo<StoreValue>(() => ({ state, dispatch }), [state]);
  if (initialization.autosaveBlocked) {
    return (
      <div className="warning-list error" role="alert">
        <h1>Gespeicherter Zustand konnte nicht sicher geladen werden</h1>
        <p>{initialization.loadError}</p>
        <p>Die vorhandenen Daten wurden nicht ueberschrieben. Automatisches Speichern ist fuer diesen Start deaktiviert.</p>
      </div>
    );
  }
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const value = useContext(StoreContext);
  if (!value) {
    throw new Error('useStore muss innerhalb von StoreProvider verwendet werden.');
  }
  return value;
}
